// Filter Drawer - AJAX Implementation (No Page Reload)

class FilterDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.drawer = this;
    this.panel = this.querySelector('.filter-drawer__panel');
    this.overlay = this.querySelector('.filter-drawer__overlay');
    this.closeButton = this.querySelector('.filter-drawer__close');
    this.form = this.querySelector('.filter-drawer__form');
    this.clearButton = this.querySelector('.filter-drawer__button--clear');
    this.applyButton = this.querySelector('.filter-drawer__button--apply');
    this.sectionId = this.dataset.sectionId;
    this.isLoading = false;

    this.setupEventListeners();
    this.setupAccordions();
  }

  // Null-safe access to the Shopify Inbox chat widget's z-index. The
  // chat element may be absent (ad-blocker, slow CDN, app disabled per-
  // page, or Shopify Inbox markup changes); without this guard every
  // call site below would TypeError on null.style and tear down the
  // handler invoking it. On the fetchFilteredResults() path the catch
  // then falls back to window.location.href = url — a full page reload
  // with a brief blank during nav. Pass null to clear the override,
  // a string value to set it.
  setShopifyChatZIndex(value) {
    const chat = document.getElementById('shopify-chat');
    if (!chat) return;
    if (value === null) {
      chat.style.removeProperty('z-index');
    } else {
      chat.style.zIndex = value;
    }
  }

  setupEventListeners() {
    // Prevent ALL form submissions
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }, true);

      this.form.onsubmit = function() {
        return false;
      };
    }

    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
        this.setShopifyChatZIndex(null);
      });
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
        this.setShopifyChatZIndex(null);
      });
    }

    // Clear filters
    if (this.clearButton) {
      this.clearButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.clearFilters();
        return false;
      });
    }

    // Apply filters
    if (this.applyButton) {
      this.applyButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.applyFilters();
        return false;
      }, true);

      this.applyButton.onclick = (e) => {
        e.preventDefault();
        this.applyFilters();
        return false;
      };
    }

    // Escape key to close
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.classList.contains('active')) {
        this.close();
        this.setShopifyChatZIndex(null);
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  setupAccordions() {
    const accordions = this.querySelectorAll('.filter-drawer__group');
    accordions.forEach(accordion => {
      const summary = accordion.querySelector('.filter-drawer__group-summary');
      if (summary) {
        summary.addEventListener('click', (e) => {
          // Let native details/summary behavior work
        });
      }
    });
  }

  open() {
    this.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.setShopifyChatZIndex('-1');
    setTimeout(() => {
      this.trapFocus();
    }, 100);
  }

  close() {
    this.classList.remove('active');
    document.body.style.overflow = '';
    this.setShopifyChatZIndex(null);

    // Return focus to the trigger that opened the drawer. The header renders a
    // mobile and a desktop trigger (only one visible per breakpoint), so the
    // first match in the document is not necessarily the one the user pressed —
    // and focusing a display:none button is a silent no-op that strands focus
    // on <body>. window.__filterDrawerLastTrigger is set by the delegated click
    // handler at the bottom of this file.
    const trigger = window.__filterDrawerLastTrigger;
    if (trigger && trigger.isConnected && trigger.offsetParent !== null) {
      trigger.focus();
      return;
    }
    const visible = FilterDrawer.triggers().find((el) => el.offsetParent !== null);
    if (visible) visible.focus();
  }

  // Every .filter-drawer-trigger on the page. Collection pages render two (the
  // header's mobile + desktop instances), so anything that writes to a trigger
  // must write to all of them — a single querySelector would update whichever
  // happens to come first in the DOM and leave the other stale.
  static triggers() {
    return Array.from(document.querySelectorAll('.filter-drawer-trigger'));
  }

  // Copy the freshly-fetched trigger's inner markup onto every live trigger.
  // Both header instances render identical inner markup, so sourcing from the
  // fetched document's first match is safe.
  static syncTriggersFrom(doc) {
    const source = doc.querySelector('.filter-drawer-trigger');
    if (!source) return;
    FilterDrawer.triggers().forEach((trigger) => {
      trigger.innerHTML = source.innerHTML;
    });
  }

  // The sort the grid is actually in, read from the URL the way Shopify reads it
  // (verified on live 2026-09-29): the LAST sort_by wins, and a value that is not
  // one of the select's options means the collection's default order, as does no
  // sort_by at all. Both cases are real here. The Best Sellers sub-filter writes
  // sort_by=featured, which Shopify ignores, and the TOPS pill carries its own
  // sort_by=manual, so a sub-filter's sort lands second in the URL.
  static effectiveSort(select, params) {
    const options = Array.from(select.options, (option) => option.value);
    const requested = params.getAll('sort_by').pop();
    if (options.includes(requested)) return requested;
    const fallback = select.dataset.defaultSort;
    return options.includes(fallback) ? fallback : options[0] || '';
  }

  // Point the drawer's sort control at that sort and repaint its label. The label
  // is repainted every time, not only when the value changes: assigning a value
  // no option carries (featured) used to leave the select with nothing selected
  // and the label still naming the previous sort, so after CARGO > New Arrivals >
  // SHORTS the drawer read "Date, new to old" over a grid in featured order.
  static syncSortControl() {
    const select = document.querySelector('#SortBy-drawer');
    if (!select) return;
    const value = FilterDrawer.effectiveSort(select, new URLSearchParams(window.location.search));
    if (select.value !== value) select.value = value;
    FilterDrawer.updateSortLabel(select);
  }

  static updateSortLabel(select) {
    const label = document.querySelector('[data-select-text]');
    const option = select.options[select.selectedIndex];
    if (label && option) label.textContent = option.text;
  }

  clearFilters() {
    if (this.isLoading) return;

    const checkboxes = this.form.querySelectorAll('input[type="checkbox"]:checked');
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    const priceInputs = this.form.querySelectorAll('.filter-drawer__price-input');
    priceInputs.forEach(input => {
      input.value = '';
    });

    this.applyFilters();
  }

  applyFilters() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoading();

    const formData = new FormData(this.form);
    const searchParams = new URLSearchParams(window.location.search);

    // Clear only what this drawer's form owns — and for checkbox groups, only the
    // VALUES it can actually represent.
    //
    // Clearing by param NAME alone (what this did until 2026-08-10) wiped values a
    // DIFFERENT control had set under the same param. Live case: the CARGO
    // quick-filter pill writes `filter.p.tag=Cargo`, and the drawer's Sale group
    // owns `filter.p.tag` with exactly one option, `sale` — so applying any drawer
    // filter dropped the shopper out of Cargo with no visible cause. Same exposure
    // for `filter.p.m.custom.filters_category`, which the category pills and the
    // drawer's Category group share.
    //
    // A value with no matching input came from elsewhere, so it is kept. A value
    // the form DOES offer is cleared and then re-added from the form below, which
    // is what lets un-ticking a box actually remove it.
    const optionValuesByKey = new Map();
    const scalarKeys = new Set();
    this.form.querySelectorAll('[name]').forEach((input) => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (!optionValuesByKey.has(input.name)) optionValuesByKey.set(input.name, new Set());
        optionValuesByKey.get(input.name).add(input.value);
      } else {
        // Price inputs, selects, hidden fields: free-form, so there is no value set
        // to compare against — clear by name as before.
        scalarKeys.add(input.name);
      }
    });
    scalarKeys.add('page');
    scalarKeys.forEach((key) => searchParams.delete(key));

    optionValuesByKey.forEach((formValues, key) => {
      const foreignValues = searchParams.getAll(key).filter((value) => !formValues.has(value));
      searchParams.delete(key);
      foreignValues.forEach((value) => searchParams.append(key, value));
    });

    // Add new filter params
    for (let [key, value] of formData.entries()) {
      if (value) {
        searchParams.append(key, value);
      }
    }

    // Rewrite sort_by only when the shopper picked a different sort in the drawer;
    // otherwise leave it exactly as the nav wrote it. Best Sellers' sort_by=featured
    // and the TOPS pill's sort_by=manual are how those buttons recognise themselves
    // in the URL (matchesButton in collection-quick-filters.js), so swapping either
    // for the equivalent option value would highlight the wrong pill afterwards.
    const sortSelect = document.querySelector('#SortBy-drawer');
    if (sortSelect && sortSelect.value && sortSelect.value !== FilterDrawer.effectiveSort(sortSelect, searchParams)) {
      searchParams.set('sort_by', sortSelect.value);
    }

    // Build URL
    const queryString = searchParams.toString();
    const url = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

    // Update browser URL without reload. Store searchParams (matching the shape
    // FacetFiltersForm pushes) so Back/Forward to this entry re-renders the
    // correct filtered grid, and keep FacetFiltersForm's dedupe marker in sync
    // so its popstate handler won't skip the re-render — the drawer renders the
    // grid itself here, bypassing FacetFiltersForm.renderPage.
    window.history.pushState({ searchParams: queryString, path: url }, '', url);
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.searchParamsPrev = queryString;
    }

    // Fetch filtered results
    this.fetchFilteredResults(url);
  }

  async fetchFilteredResults(url) {
    try {
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      this.updateProductGrid(doc);
      this.updateFilterCounts(doc);

      // Adopt the fetched trigger's markup FIRST, then write the count. The order
      // matters: the trigger ships an EMPTY badge from Liquid, so syncing after
      // counting would wipe the number straight back out. (It used to run in that
      // order, back when the badge was server-rendered — which is why the count
      // never survived a drawer apply once the trigger moved into the header.)
      FilterDrawer.syncTriggersFrom(doc);
      this.updateActiveFilterCount();

      this.close();
      this.setShopifyChatZIndex(null);
      // this.scrollToProducts();
    } catch (error) {
      console.error('Error fetching filtered results:', error);
      window.location.href = url;
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  // Grid, pagination and count are looked up inside <main> only, on the live page
  // AND on the fetched one. The selectors are loose, and the layout renders things
  // before <main> that match them: the empty cart drawer's placeholder cards are a
  // `ul.product-grid`, so with an empty cart Apply used to swap those placeholders
  // for the fetched ones and leave the real grid untouched (2026-11-03).
  static mainQuery(root, selector) {
    const main = root.getElementById ? root.getElementById('MainContent') : null;
    return (main || root).querySelector(selector);
  }

  updateProductGrid(doc) {
    const gridSelector = '#product-grid, .product-grid, [id*="product-grid"]';
    const newProductGrid = FilterDrawer.mainQuery(doc, gridSelector);
    const currentProductGrid = FilterDrawer.mainQuery(document, gridSelector);

    if (newProductGrid && currentProductGrid) {
      currentProductGrid.style.opacity = '0.5';
      currentProductGrid.style.transition = 'opacity 0.3s ease';

      setTimeout(() => {
        currentProductGrid.innerHTML = newProductGrid.innerHTML;
        currentProductGrid.style.opacity = '1';
        this.reinitializeProductCards();
      }, 300);
    }

    const newPagination = FilterDrawer.mainQuery(doc, '.pagination, [class*="pagination"]');
    const currentPagination = FilterDrawer.mainQuery(document, '.pagination, [class*="pagination"]');

    if (newPagination && currentPagination) {
      currentPagination.innerHTML = newPagination.innerHTML;
    }

    const newProductCount = FilterDrawer.mainQuery(doc, '.product-count, [class*="product-count"]');
    const currentProductCount = FilterDrawer.mainQuery(document, '.product-count, [class*="product-count"]');

    if (newProductCount && currentProductCount) {
      currentProductCount.innerHTML = newProductCount.innerHTML;
    }
  }

  updateFilterCounts(doc) {
    // Match groups by their data-filter-param key, not by DOM position. Accordions
    // appear/disappear between views (e.g. Waist/Length have no values under
    // Accessories) and the "Sale" group renders last — both shift indices, so
    // positional pairing would sync counts onto the wrong group.
    const currentGroupsByParam = new Map();
    this.querySelectorAll('.filter-drawer__group').forEach((group) => {
      const key = group.dataset.filterParam;
      if (key) currentGroupsByParam.set(key, group);
    });

    const newFilterGroups = doc.querySelectorAll('.filter-drawer__group');

    newFilterGroups.forEach((newGroup) => {
      const currentGroup = currentGroupsByParam.get(newGroup.dataset.filterParam);
      if (!currentGroup) return;

      const newCheckboxes = newGroup.querySelectorAll('.filter-drawer__checkbox-label');
      const currentCheckboxes = currentGroup.querySelectorAll('.filter-drawer__checkbox-label');

      newCheckboxes.forEach((newCheckbox, checkboxIndex) => {
        const currentCheckbox = currentCheckboxes[checkboxIndex];
        if (!currentCheckbox) return;

        const newCount = newCheckbox.querySelector('.filter-drawer__checkbox-count');
        const currentCount = currentCheckbox.querySelector('.filter-drawer__checkbox-count');

        if (newCount && currentCount) {
          currentCount.textContent = newCount.textContent;
        }

        const newInput = newCheckbox.querySelector('input');
        const currentInput = currentCheckbox.querySelector('input');

        if (newInput && currentInput) {
          currentInput.checked = newInput.checked;
          currentCheckbox.classList.toggle('active', newInput.checked);

          if (newInput.disabled) {
            currentCheckbox.classList.add('disabled');
            currentInput.disabled = true;
          } else {
            currentCheckbox.classList.remove('disabled');
            currentInput.disabled = false;
          }
        }
      });
    });

    // Sync price range inputs
    const newPriceInputs = doc.querySelectorAll('.filter-drawer__price-input');
    const currentPriceInputs = this.querySelectorAll('.filter-drawer__price-input');
    newPriceInputs.forEach((newInput, i) => {
      const currentInput = currentPriceInputs[i];
      if (currentInput) currentInput.value = newInput.value || '';
    });

    // Sync the sort control from the URL, not from the fetched page: its select
    // marks nothing selected when the URL's sort is one Shopify ignores.
    FilterDrawer.syncSortControl();
  }

  // Refresh drawer state (checkboxes, prices, sort, trigger badge) from a URL
  // without touching the product grid. NOTE: the drawer body (#FilterDrawer with
  // its .filter-drawer__group markup) is rendered in the LAYOUT, not inside the
  // grid section FacetFiltersForm fetches (`?section_id=`), so its markup is only
  // available from a full-page fetch — we cannot reuse facets' section doc or a
  // per-section fetch here. Used when an external actor (quick-filter pill, sort,
  // back/forward) updated the URL and already re-rendered the grid.
  async syncFromUrl(url) {
    try {
      const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.updateFilterCounts(doc);

      // Sync before counting — see the note in fetchFilteredResults().
      FilterDrawer.syncTriggersFrom(doc);
      this.updateActiveFilterCount();
    } catch (error) {
      console.error('Error syncing filter drawer:', error);
    }
  }

  // Refresh drawer state from the grid-section document FacetFiltersForm ALREADY
  // fetched (dispatched via `facets:filters-updated`) — no second full-page
  // round-trip. The section's `.facets__label` elements carry the same filter
  // values as the drawer's checkboxes, matched by input name+value (robust to
  // ordering and to filters appearing/disappearing between category views). Sort
  // + price are read from the URL because those drawer controls aren't in the
  // grid section. Drawer checkboxes with no matching section facet (e.g. the
  // hardcoded Sale tag group when no tag facet is configured) are left untouched,
  // never zeroed.
  syncFromFacets(doc) {
    if (doc) {
      const facetState = new Map();
      doc.querySelectorAll('.facets__label').forEach((label) => {
        const input = label.querySelector('input[name]');
        if (!input || !input.name || input.value === '') return;
        let count = parseInt(label.dataset.count, 10);
        if (Number.isNaN(count)) {
          const match = label.textContent.match(/\((\d+)\)/);
          count = match ? parseInt(match[1], 10) : null;
        }
        facetState.set(`${input.name}=${input.value}`, {
          count,
          checked: input.checked,
          disabled: input.disabled || label.classList.contains('disabled'),
        });
      });

      if (facetState.size) {
        this.querySelectorAll('.filter-drawer__checkbox-label').forEach((labelEl) => {
          const input = labelEl.querySelector('input[name]');
          if (!input || !input.name) return;
          const state = facetState.get(`${input.name}=${input.value}`);
          if (!state) return;

          const countEl = labelEl.querySelector('.filter-drawer__checkbox-count');
          if (countEl && state.count !== null) countEl.textContent = `(${state.count})`;

          input.checked = state.checked;
          labelEl.classList.toggle('active', state.checked);
          input.disabled = state.disabled;
          labelEl.classList.toggle('disabled', state.disabled);
        });
      }
    }

    this.syncControlsFromUrl();
    this.updateActiveFilterCount();
  }

  // Sync the drawer's sort dropdown + price inputs from the current URL — these
  // controls live in the drawer (layout), not the fetched grid section.
  syncControlsFromUrl() {
    const params = new URLSearchParams(window.location.search);

    FilterDrawer.syncSortControl();

    this.querySelectorAll('.filter-drawer__price-input').forEach((input) => {
      if (input.name) input.value = params.get(input.name) || '';
    });
  }

  // Params driven by the quick-filter nav — the category pills and the sub-filter
  // buttons. Choosing one of those is page navigation, not a filter the shopper
  // applied in the drawer, so the badge must not count them (client, 2026-08-10).
  //
  // Derived from the nav's own buttons rather than hardcoded, so a customizer-added
  // pill is covered for free — the live CARGO pill filters by `filter.p.tag`, which
  // no hardcoded list would have predicted. data-clear is deliberately NOT read:
  // clear_params is dead in v3 (buildSearchParams never reads it), and treating it
  // as nav-owned would over-exclude.
  //
  // ⚠️ Two of these params are ALSO drawer groups: `filter.p.m.custom.filters_category`
  // is the drawer's Category group as well as the pills, and `filter.p.tag` is its
  // Sale group as well as the Sale sub-filter. Both produce a byte-identical URL
  // whichever control set them, so a Category or Sale choice made INSIDE the drawer
  // is not counted either. That is a real limit, not an oversight: the URL carries
  // no record of which control wrote a param. It is also the less surprising side
  // to err on, since those two selections are already shown as an active pill.
  static navOwnedParams() {
    const params = new Set();
    document.querySelectorAll('[data-quick-filter-btn], [data-sub-filter-btn]').forEach((button) => {
      [button.dataset.query, button.dataset.queryAllOverride].forEach((queryString) => {
        if (!queryString) return;
        new URLSearchParams(queryString).forEach((_value, key) => params.add(key));
      });
    });
    // A nav control can also claim a param only while it is the one showing that
    // param's state: the wide-leg row (collection-leg-filter.js) sets
    // data-nav-param while one of its bands is the applied Leg Opening filter, so
    // a band counts as navigation too. A drawer choice that matches no band is
    // not claimed and still counts.
    document.querySelectorAll('[data-nav-param]').forEach((element) => {
      if (element.dataset.navParam) params.add(element.dataset.navParam);
    });
    return params;
  }

  updateActiveFilterCount() {
    if (!this.form) return;

    const navParams = FilterDrawer.navOwnedParams();
    const checkedFilters = Array.from(
      this.form.querySelectorAll('input[type="checkbox"]:checked')
    ).filter((input) => !navParams.has(input.name));

    const priceInputs = this.form.querySelectorAll('.filter-drawer__price-input');
    let priceFilterActive = false;

    priceInputs.forEach(input => {
      if (input.value) priceFilterActive = true;
    });

    // A min and a max are one price filter, not two.
    const totalActive = checkedFilters.length + (priceFilterActive ? 1 : 0);

    // Update EVERY trigger, not just the first — collection pages carry the
    // header's mobile and desktop instances, and the hidden one becomes visible
    // on rotate/resize, so a stale badge there would surface later.
    FilterDrawer.triggers().forEach((trigger) => {
      let countBadge = trigger.querySelector('[data-filter-drawer-count]');

      // The badge normally ships with the trigger (see filter-drawer-trigger.liquid).
      // Recreate it only if a trigger somehow renders without one, so this never
      // silently no-ops. It goes inside the label span, never on the trigger
      // itself: the trigger is a flex container and a flex item ignores
      // vertical-align, which paints the digit centred instead of raised.
      if (!countBadge) {
        countBadge = document.createElement('sup');
        countBadge.className = 'filter-drawer-trigger__count';
        countBadge.setAttribute('data-filter-drawer-count', '');
        countBadge.setAttribute('aria-hidden', 'true');
        (trigger.querySelector('.filter-drawer-trigger__text') || trigger).appendChild(countBadge);
      }

      // Emptied rather than removed: `.filter-drawer-trigger__count:empty` hides it,
      // matching how the header cart badge behaves at zero.
      countBadge.textContent = totalActive > 0 ? String(totalActive) : '';

      // The visible badge is aria-hidden, so the count reaches screen readers only
      // through this. data-count-label holds the translated string with the number
      // swapped for a sentinel, so no English is hardcoded here.
      const a11yLabel = trigger.querySelector('[data-filter-drawer-count-a11y]');
      if (!a11yLabel) return;
      const template = a11yLabel.dataset.countLabel;
      a11yLabel.textContent =
        totalActive > 0 && template ? template.replace('[count]', String(totalActive)) : '';
    });
  }

  reinitializeProductCards() {
    if (typeof window.initializeQuickAdd === 'function') {
      window.initializeQuickAdd();
    }

    document.dispatchEvent(new CustomEvent('filterDrawer:productsUpdated'));
  }

  scrollToProducts() {
    const productGrid = FilterDrawer.mainQuery(document, '#product-grid, .product-grid, [id*="product-grid"]');
    if (productGrid) {
      const offset = 100;
      const elementPosition = productGrid.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  showLoading() {
    if (this.applyButton) {
      this.applyButton.disabled = true;
      this.applyButton.style.opacity = '0.6';
      this.applyButton.textContent = 'Applying...';
    }

    const productGridContainer = document.querySelector('#ProductGridContainer, [id*="ProductGridContainer"]');
    if (productGridContainer) {
      let loadingOverlay = productGridContainer.querySelector('.loading-overlay');
      if (!loadingOverlay) {
        loadingOverlay = document.createElement('div');
        loadingOverlay.className = 'loading-overlay';
        loadingOverlay.innerHTML = '<div class="loading-spinner"></div>';
        productGridContainer.style.position = 'relative';
        productGridContainer.appendChild(loadingOverlay);
      }
      loadingOverlay.style.display = 'flex';
      loadingOverlay.style.opacity = '1';
    }
  }

  hideLoading() {
    if (this.applyButton) {
      this.applyButton.disabled = false;
      this.applyButton.style.opacity = '1';

      const applyText = this.applyButton.dataset.originalText || 'Apply';
      this.applyButton.textContent = applyText;
    }

    const loadingOverlay = document.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.style.opacity = '0';
      setTimeout(() => {
        loadingOverlay.style.display = 'none';
      }, 300);
    }
  }

  trapFocus() {
    if (!this.panel) return;

    const focusableElements = this.panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (firstElement) {
      firstElement.focus();
    }

    this.tabHandler = (e) => {
      if (e.key !== 'Tab') return;
      if (!this.classList.contains('active')) return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    this.panel.addEventListener('keydown', this.tabHandler);
  }

  disconnectedCallback() {
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
    }
    if (this.tabHandler && this.panel) {
      this.panel.removeEventListener('keydown', this.tabHandler);
    }
  }
}

// Define custom element
if (!customElements.get('filter-drawer')) {
  customElements.define('filter-drawer', FilterDrawer);
}

// Track if already initialized
let filterDrawerInitialized = false;

// Initialize filter drawer
function initFilterDrawer(force = false) {
  if (filterDrawerInitialized && !force) return;

  const drawer = document.querySelector('filter-drawer');

  if (!drawer) return;

  filterDrawerInitialized = true;

  // Fill the badge for the landing URL. The trigger renders it empty (the count
  // has to exclude nav-owned params, a set only known once the grid section's
  // buttons are in the DOM), so without this a shared or refreshed link carrying
  // drawer filters would show FILTER with no number until the next interaction.
  // Runs here rather than in connectedCallback for that same reason: the drawer
  // lives in the layout and can connect before the grid section exists.
  if (typeof drawer.updateActiveFilterCount === 'function') {
    drawer.updateActiveFilterCount();
  }

  // The Filter trigger button is handled by a delegated document-level click
  // listener (see bindFilterDrawerTrigger below), so it keeps working even after
  // an AJAX filter apply or a theme-editor section reload replaces the trigger.
  // The previous code bound the listener directly to the element, so any
  // re-render that swapped the trigger left a dead button.

  // Sort dropdown inside drawer - auto-apply on change
  const sortSelect = document.querySelector('#SortBy-drawer');
  if (sortSelect) {
    // Initialize from the URL too. For a sort Shopify ignores, the server marks no
    // option selected and the browser falls back to the first one, which is only
    // right while the first option happens to be the collection's default.
    FilterDrawer.syncSortControl();

    sortSelect.addEventListener('change', (e) => {
      FilterDrawer.updateSortLabel(sortSelect);

      // Automatically apply filters when sort changes
      if (drawer && drawer.applyFilters) {
        drawer.applyFilters();
      }
    });
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initFilterDrawer());
} else {
  initFilterDrawer();
}

// Backup initialization (only if not already initialized)
setTimeout(() => {
  if (!filterDrawerInitialized) {
    initFilterDrawer();
  }
}, 500);

// Allow re-binding when drawer markup is replaced dynamically
window.refreshFilterDrawerTriggers = function refreshFilterDrawerTriggers() {
  filterDrawerInitialized = false;
  initFilterDrawer(true);
};

// Delegated Filter-button handler — bound once for the page lifetime. Because it
// lives on `document` (not the trigger element), it keeps working after any
// re-render that swaps out the .filter-drawer-trigger: an AJAX filter apply,
// quick-filter pills, or a theme-editor section reload. The drawer is re-queried
// per click so a re-rendered drawer element is handled too.
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('.filter-drawer-trigger');
  if (!trigger) return;
  e.preventDefault();
  // Remember which trigger opened the drawer so close() can restore focus to it
  // rather than to whichever instance happens to be first in the DOM.
  window.__filterDrawerLastTrigger = trigger;
  const drawer = document.querySelector('filter-drawer');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
  }
});

// Handle browser back/forward buttons.
//
// On v2 collection pages FacetFiltersForm owns the product-grid re-render on
// history navigation (it replaces #ProductGridContainer from a ?section_id=
// fetch). The drawer must NOT also fetch/replace the grid here — two engines
// writing the overlapping #product-grid node race and can paint stale/unfiltered
// results. So when FacetFiltersForm is present we only sync the drawer's own UI
// (checkboxes, counts, trigger badge) from the restored URL; on a drawer-only
// page the drawer remains the sole grid engine and re-renders as before.
window.addEventListener('popstate', (e) => {
  const drawer = document.querySelector('filter-drawer');
  if (!drawer) {
    window.location.reload();
    return;
  }
  // On v2 pages FacetFiltersForm re-renders the grid on history nav and dispatches
  // `facets:filters-updated`, which refreshes the drawer (see listener below) — so
  // there's nothing to do here. On a drawer-only page the drawer is the sole grid
  // engine and must re-fetch.
  if (!document.querySelector('facet-filters-form')) {
    drawer.fetchFilteredResults(window.location.href);
  }
});

// External URL changes (quick-filter pill clicks, sort, back/forward) — refresh
// the drawer's checkbox counts by REUSING the grid section FacetFiltersForm just
// fetched, rather than a second full-page round-trip. facets.js dispatches this
// after each grid re-render with the parsed section document.
document.addEventListener('facets:filters-updated', (event) => {
  const drawer = document.querySelector('filter-drawer');
  if (drawer && typeof drawer.syncFromFacets === 'function') {
    drawer.syncFromFacets(event.detail && event.detail.doc);
  }
});

// Handle active filter tags removal
document.addEventListener('click', (e) => {
  if (e.target.closest('.active-filter-tag')) {
    e.preventDefault();
    const tag = e.target.closest('.active-filter-tag');
    const filterParam = tag.dataset.filterParam;
    const filterValue = tag.dataset.filterValue;

    if (filterParam && filterValue) {
      const drawer = document.querySelector('filter-drawer');

      if (drawer) {
        const checkbox = drawer.querySelector(`input[name="${filterParam}"][value="${filterValue}"]`);
        if (checkbox) {
          checkbox.checked = false;
        }
        drawer.applyFilters();
      } else {
        const url = new URL(window.location.href);
        const params = url.searchParams.getAll(filterParam);

        url.searchParams.delete(filterParam);
        params.forEach(param => {
          if (param !== filterValue) {
            url.searchParams.append(filterParam, param);
          }
        });

        window.location.href = url.toString();
      }
    }
  }
});
